import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MetaTags } from "@/components/MetaTags";
import { SocialShare } from "@/components/SocialShare";

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { isPremium, isPro } = useSubscription();
  
  // Checkout confirmation dialog state
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{ priceId: string; planType: string } | null>(null);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [immediateStartAccepted, setImmediateStartAccepted] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsLoggedIn(!!user);
    
    if (user) {
      const { data: subscription } = await supabase
        .from("subscriptions")
        .select("plan_type, status")
        .eq("user_id", user.id)
        .eq("status", "active")
        .single();
      
      if (subscription) {
        setCurrentPlan(subscription.plan_type);
      }
    }
  };

  const initiateCheckout = (priceId: string, planType: string) => {
    if (!isLoggedIn) {
      toast.error("Bitte melden Sie sich zuerst an");
      navigate("/auth");
      return;
    }

    // Show confirmation dialog
    setSelectedPlan({ priceId, planType });
    setShowCheckoutDialog(true);
    setAgbAccepted(false);
    setImmediateStartAccepted(false);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    
    if (!agbAccepted || !immediateStartAccepted) {
      toast.error("Bitte bestätigen Sie beide Zustimmungen");
      return;
    }

    setLoading(selectedPlan.planType);
    setShowCheckoutDialog(false);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { 
          priceId: selectedPlan.priceId, 
          planType: selectedPlan.planType,
          agbAccepted: true,
          immediateStartAccepted: true,
          acceptanceTimestamp: new Date().toISOString()
        },
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast.error("Fehler beim Checkout: " + error.message);
    } finally {
      setLoading(null);
      setSelectedPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session");

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Error opening portal:", error);
      toast.error("Fehler beim Öffnen des Kundenportals: " + error.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const plans = [
    {
      name: "Basic",
      price: "Kostenlos",
      priceId: null,
      planType: "free",
      description: "Perfekt für den Start",
      features: [
        "1 Notfallprofil",
        "Standard QR-Code",
        "Vor- & Nachname, Geburtsdatum, Blutgruppe",
        "Bis zu 2 Allergien",
        "Bis zu 2 Vorerkrankungen",
        "Bis zu 2 Medikamente",
        "Bis zu 2 Notfallkontakte",
        "Zusätzliche Notizen",
        "Email-Bestätigung alle 30 Tage",
      ],
      cta: "Kostenlos starten",
      popular: false,
    },
    {
      name: "Premium",
      price: "€4,99",
      period: "/Monat",
      priceId: "price_1SU30VKllWK9Zu7L6kXEflzZ",
      planType: "premium",
      description: "Für mehr Sicherheit",
      features: [
        "2 Notfallprofile",
        "Individualisierbare QR-Codes",
        "Unbegrenzte Einträge (Allergien, Medikamente, etc.)",
        "Detaillierte Medikamenten-Notizen",
        "Allergie-Notizen",
        "Schubstatus für Erkrankungen",
        "Erweiterte Krankheits-Notizen",
        "Notfall-Benachrichtigungen",
        "Scan-Statistiken",
        "Passwortschutz",
        "PDF-Export",
        "Automatische Cloud-Backups",
        "Prioritäts-Support",
        "Email-Bestätigung alle 180 Tage",
      ],
      cta: "Jetzt upgraden",
      popular: true,
    },
    {
      name: "Pro/Familie",
      price: "€9,99",
      period: "/Monat",
      priceId: "price_1SU34LKllWK9Zu7LK9RjKz43",
      planType: "pro",
      description: "Für die ganze Familie",
      features: [
        "Alle Premium-Features",
        "Bis zu 5 QR-Codes",
        "Automatische Cloud-Backups",
        "Keine Email-Bestätigung nötig",
      ],
      cta: "Pro werden",
      popular: false,
    },
  ];

  return (
    <>
      <MetaTags
        title="Preise - MediQR Notfall QR-Code Pläne"
        description="Wählen Sie den passenden MediQR Plan für Ihre Familie. Kostenloser Basic-Plan verfügbar. Premium und Pro Pläne mit erweiterten Funktionen ab €4,99/Monat."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "MediQR",
          "description": "Digitale Notfallhilfe durch personalisierte QR-Codes",
          "offers": [
            {
              "@type": "Offer",
              "name": "Basic Plan",
              "price": "0",
              "priceCurrency": "EUR"
            },
            {
              "@type": "Offer",
              "name": "Premium Plan",
              "price": "4.99",
              "priceCurrency": "EUR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "4.99",
                "priceCurrency": "EUR",
                "unitText": "MONTH"
              }
            },
            {
              "@type": "Offer",
              "name": "Pro Plan",
              "price": "9.99",
              "priceCurrency": "EUR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "9.99",
                "priceCurrency": "EUR",
                "unitText": "MONTH"
              }
            }
          ]
        }}
      />
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
        <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Wählen Sie Ihren Plan</h1>
          <p className="text-xl text-muted-foreground">
            Schützen Sie sich und Ihre Familie mit MediQR
          </p>
          
          {isLoggedIn && (isPremium || isPro) && (
            <div className="mt-6">
              <Button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                variant="outline"
                size="lg"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Öffnet Kundenportal...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Abo verwalten
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.planType}
              className={`relative ${
                plan.popular ? "border-primary shadow-lg scale-105" : ""
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
                  Beliebteste Wahl
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.period && (
                    <span className="text-muted-foreground">{plan.period}</span>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  variant={plan.popular ? "default" : "outline"}
                  size="lg"
                  onClick={() => {
                    if (plan.priceId) {
                      initiateCheckout(plan.priceId, plan.planType);
                    } else {
                      navigate("/auth");
                    }
                  }}
                  disabled={loading === plan.planType || currentPlan === plan.planType}
                >
                  {loading === plan.planType
                    ? "Wird geladen..."
                    : currentPlan === plan.planType
                    ? "Aktueller Plan"
                    : plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Alle Pläne beinhalten: Verschlüsselte Datenspeicherung • DSGVO-konform • Kein Risiko
          </p>
          <Button variant="ghost" onClick={() => navigate("/")}>
            Zurück zur Startseite
          </Button>
          
          <div className="mt-12 pt-8 border-t border-border/50 max-w-2xl mx-auto">
            <SocialShare
              title="MediQR Preise - Schütze deine Familie im Notfall"
              description="Wähle den passenden MediQR Plan für deine Familie. Kostenloser Basic-Plan verfügbar oder Premium-Pläne mit erweiterten Funktionen ab €4,99/Monat."
              hashtags={["MediQR", "Notfallhilfe", "Gesundheit", "Familie"]}
            />
          </div>
        </div>
      </div>

      {/* Checkout Confirmation Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Abonnement abschließen</DialogTitle>
            <DialogDescription>
              Bitte bestätigen Sie die folgenden Bedingungen, um fortzufahren.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Durch die ausdrückliche Zustimmung zum sofortigen Leistungsbeginn erlischt Ihr Widerrufsrecht 
                gemäß § 356 Abs. 5 BGB.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="agb"
                  checked={agbAccepted}
                  onCheckedChange={(checked) => setAgbAccepted(checked === true)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="agb"
                    className="text-sm font-medium leading-relaxed cursor-pointer"
                  >
                    Ich habe die{" "}
                    <Link 
                      to="/agb" 
                      target="_blank" 
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Allgemeinen Geschäftsbedingungen (AGB)
                    </Link>
                    {" "}gelesen und akzeptiere diese. *
                  </Label>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="immediate-start"
                  checked={immediateStartAccepted}
                  onCheckedChange={(checked) => setImmediateStartAccepted(checked === true)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="immediate-start"
                    className="text-sm font-medium leading-relaxed cursor-pointer"
                  >
                    Ich stimme ausdrücklich zu, dass die Leistungserbringung sofort nach Vertragsabschluss 
                    beginnt. Mir ist bekannt, dass ich durch diese Zustimmung mein{" "}
                    <Link 
                      to="/widerrufsrecht" 
                      target="_blank" 
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Widerrufsrecht
                    </Link>
                    {" "}verliere, sobald die Leistungserbringung beginnt. *
                  </Label>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              * Pflichtfelder - Beide Zustimmungen sind erforderlich, um fortzufahren.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCheckoutDialog(false);
                setSelectedPlan(null);
                setAgbAccepted(false);
                setImmediateStartAccepted(false);
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={!agbAccepted || !immediateStartAccepted || !!loading}
              className="bg-gradient-primary"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird geladen...
                </>
              ) : (
                "Zahlungspflichtig bestellen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </div>
    </>
  );
};

export default Pricing;
